import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { SubmissionPayPal } from '../components/SubmissionPayPal';
import {
  Upload, Plus, Trash2, Check, AlertCircle,
  ArrowLeft, ArrowRight, Loader2, CalendarDays, X, CreditCard, User, Mail, Phone
} from 'lucide-react';
import SEOHead from '../components/SEOHead';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_IMAGE_SIZE = 3 * 1024 * 1024;

const GuestSubmissionForm = () => {
  const { callId } = useParams();
  const navigate = useNavigate();
  const [call, setCall] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState(1);

  // Guest info
  const [guestName, setGuestName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [guestPhone, setGuestPhone] = useState('');

  // Artworks
  const [artworks, setArtworks] = useState([
    { title: '', size: '', medium: '', medium_category_id: '', price: '', image_urls: [], uploading: false }
  ]);

  // Post-submit state
  const [pendingSubmission, setPendingSubmission] = useState(null);
  const [guestToken, setGuestToken] = useState(null);
  const [confirmationId, setConfirmationId] = useState(null);

  const fileInputRefs = useRef({});

  const fetchCall = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/submissions/guest/call-info/${callId}`);
      if (!res.ok) throw new Error('Call not found');
      const data = await res.json();
      setCall(data);
      setCategories(data.categories || []);
    } catch {
      toast.error('Could not load submission details');
    } finally {
      setLoading(false);
    }
  }, [callId]);

  useEffect(() => { fetchCall(); }, [fetchCall]);

  const handleImageUpload = async (artworkIndex, files) => {
    const fileArr = Array.from(files);
    const current = artworks[artworkIndex];
    for (const file of fileArr) {
      if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
        toast.error(`"${file.name}" is not supported. Use JPEG, PNG, or WebP.`);
        return;
      }
      if (file.size > MAX_IMAGE_SIZE) {
        toast.error(`"${file.name}" is too large. Max 3MB.`);
        return;
      }
    }
    const updated = [...artworks];
    updated[artworkIndex] = { ...current, uploading: true };
    setArtworks(updated);
    const newUrls = [...current.image_urls];
    for (const file of fileArr) {
      const fd = new FormData();
      fd.append('file', file);
      try {
        const res = await fetch(`${API_URL}/api/submissions/guest/upload-image`, { method: 'POST', body: fd });
        if (!res.ok) { const err = await res.json(); toast.error(err.detail || 'Upload failed'); continue; }
        const data = await res.json();
        newUrls.push(data.url);
      } catch { toast.error(`Failed to upload "${file.name}"`); }
    }
    const final_ = [...artworks];
    final_[artworkIndex] = { ...current, image_urls: newUrls, uploading: false };
    setArtworks(final_);
  };

  const removeImage = (ai, ii) => { const u = [...artworks]; u[ai].image_urls.splice(ii, 1); setArtworks(u); };
  const addArtwork = () => {
    if (artworks.length >= (call?.max_artworks || 5)) { toast.error(`Max ${call?.max_artworks || 5} artworks`); return; }
    setArtworks([...artworks, { title: '', size: '', medium: '', medium_category_id: '', price: '', image_urls: [], uploading: false }]);
  };
  const removeArtwork = (i) => { if (artworks.length <= 1) return; setArtworks(artworks.filter((_, idx) => idx !== i)); };
  const updateArtwork = (i, f, v) => { const u = [...artworks]; u[i] = { ...u[i], [f]: v }; setArtworks(u); };

  const validateContactInfo = () => {
    if (!guestName.trim()) { toast.error('Please enter your name'); return false; }
    if (!guestEmail.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(guestEmail)) { toast.error('Please enter a valid email'); return false; }
    return true;
  };

  const validateArtworks = () => {
    for (let i = 0; i < artworks.length; i++) {
      const aw = artworks[i];
      if (!aw.title.trim()) { toast.error(`Artwork ${i+1}: Title required`); return false; }
      if (!aw.size.trim()) { toast.error(`Artwork ${i+1}: Size required`); return false; }
      if (!aw.medium.trim()) { toast.error(`Artwork ${i+1}: Medium required`); return false; }
      if (categories.length > 0 && !aw.medium_category_id) { toast.error(`Artwork ${i+1}: Select a category`); return false; }
      if (!aw.price && aw.price !== 0) { toast.error(`Artwork ${i+1}: Price required`); return false; }
      if (aw.image_urls.length === 0) { toast.error(`Artwork ${i+1}: Upload at least one image`); return false; }
    }
    return true;
  };

  const goToStep2 = () => { if (validateContactInfo()) { setStep(2); window.scrollTo({ top: 0, behavior: 'smooth' }); } };
  const goToStep3 = () => { if (validateArtworks()) { setStep(3); window.scrollTo({ top: 0, behavior: 'smooth' }); } };

  const totalFee = call ? (
    (call.dual_tier_fees_enabled
      ? (call.non_member_fee_flat || 0) + (call.non_member_per_artwork_fee || 0) * artworks.length
      : call.fees_enabled
        ? (call.submission_fee || 0) + (call.per_artwork_fee || 0) * artworks.length
        : 0)
  ) : 0;
  const paymentRequired = totalFee > 0;

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        guest_name: guestName.trim(),
        guest_email: guestEmail.trim(),
        guest_phone: guestPhone.trim(),
        artworks: artworks.map(aw => ({
          title: aw.title.trim(), size: aw.size.trim(), medium: aw.medium.trim(),
          medium_category_id: aw.medium_category_id || '', price: parseFloat(aw.price), image_urls: aw.image_urls
        }))
      };
      const res = await fetch(`${API_URL}/api/submissions/guest/init/${callId}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload)
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail || 'Submission failed'); }
      const result = await res.json();
      setGuestToken(result.guest_token);
      setConfirmationId(result.submission?.id);

      if (result.payment_required && result.submission) {
        setPendingSubmission(result.submission);
        setStep(4);
      } else {
        setStep(5);
      }
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) { toast.error(e.message); } finally { setSubmitting(false); }
  };

  const capturePayment = async (orderId, capabilityToken) => {
    try {
      const res = await fetch(`${API_URL}/api/submissions/guest/pay/${pendingSubmission.id}/capture/${orderId}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ token: guestToken, capability_token: capabilityToken })
      });
      if (!res.ok) throw new Error('Payment capture failed');
      setStep(5);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) { toast.error(e.message); }
  };

  if (loading) return <div className="min-h-screen bg-stone-50 flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cafa-red" /></div>;
  if (!call) return null;

  const isClosed = !call.is_open;
  const deadline = new Date(call.deadline);

  // SUCCESS
  if (step === 5) {
    const manageUrl = guestToken && confirmationId ? `${window.location.origin}/submissions/guest/manage/${confirmationId}?token=${guestToken}` : null;
    return (
      <div className="min-h-screen bg-gradient-to-b from-green-50 to-white" data-testid="guest-submission-success">
        <SEOHead title="Submission Received | CAFA" />
        <div className="max-w-2xl mx-auto px-4 py-16 text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6"><Check size={40} className="text-green-600" /></div>
          <h1 className="text-3xl sm:text-4xl font-serif text-stone-900 mb-4">Submission Received!</h1>
          <p className="text-lg text-stone-600 mb-2">Thank you for submitting your artwork to</p>
          <p className="text-xl font-semibold text-stone-900 mb-4">{call.exhibition?.title}</p>
          {confirmationId && <p className="text-stone-500 mb-2">Confirmation: <strong>{confirmationId.slice(0, 8).toUpperCase()}</strong></p>}
          {pendingSubmission?.total_fee > 0 && (
            <div className="inline-block bg-green-50 border border-green-200 rounded-lg px-6 py-3 mb-4">
              <p className="text-green-800 font-medium">Payment confirmed: ${pendingSubmission.total_fee.toFixed(2)}</p>
            </div>
          )}
          <p className="text-stone-500 mb-6">A confirmation email has been sent to <strong>{guestEmail}</strong>.</p>
          {manageUrl && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-left mb-8 max-w-lg mx-auto">
              <p className="text-sm font-semibold text-blue-900 mb-1">Manage Your Submission</p>
              <p className="text-xs text-blue-700 mb-2">Bookmark this link to edit your submission before the deadline:</p>
              <a href={manageUrl} className="text-xs text-cafa-red break-all underline" data-testid="manage-link">{manageUrl}</a>
            </div>
          )}
          <Button size="lg" className="bg-cafa-red hover:bg-red-700 text-white" onClick={() => navigate('/exhibitions')}>
            Back to Exhibitions
          </Button>
        </div>
      </div>
    );
  }

  // CLOSED
  if (isClosed) {
    return (
      <div className="min-h-screen bg-stone-50" data-testid="guest-submissions-closed">
        <SEOHead title="Submissions Closed | CAFA" />
        <div className="max-w-2xl mx-auto px-4 py-16 text-center">
          <AlertCircle size={48} className="mx-auto mb-4 text-stone-400" />
          <h1 className="text-3xl font-serif text-stone-900 mb-4">Submissions Closed</h1>
          <p className="text-lg text-stone-600 mb-6">The deadline for this call has passed.</p>
          <Button variant="outline" onClick={() => navigate('/exhibitions')}>Back to Exhibitions</Button>
        </div>
      </div>
    );
  }

  const stepLabels = paymentRequired
    ? [{n:1,l:'Your Info'},{n:2,l:'Artwork'},{n:3,l:'Review'},{n:4,l:'Payment'}]
    : [{n:1,l:'Your Info'},{n:2,l:'Artwork'},{n:3,l:'Review & Submit'}];

  return (
    <div className="min-h-screen bg-gradient-to-b from-stone-50 to-white" data-testid="guest-submission-form">
      <SEOHead title="Non-Member Submission | CAFA" />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {/* Header */}
        <div className="mb-8">
          <Button variant="ghost" className="mb-4 text-stone-500" onClick={() => step === 1 ? navigate(-1) : setStep(Math.max(1, step - 1))} data-testid="back-btn">
            <ArrowLeft size={18} className="mr-1" /> Back
          </Button>
          <h1 className="text-3xl sm:text-4xl font-serif text-stone-900">Non-Member Submission</h1>
          <p className="text-lg text-stone-600 mt-2">{call.exhibition?.title}</p>
          <div className="flex items-center gap-2 mt-3 text-stone-700">
            <CalendarDays size={18} className="text-cafa-red" />
            <span className="text-base font-medium">Deadline: {deadline.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
          </div>
          {call.instructions && <p className="text-stone-500 mt-3">{call.instructions}</p>}
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-3 mb-10 flex-wrap" data-testid="progress-steps">
          {stepLabels.map(({n,l}, i) => (
            <React.Fragment key={n}>
              <div className="flex items-center gap-2">
                <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold ${step >= n ? 'bg-cafa-red text-white' : 'bg-stone-200 text-stone-500'}`}>{n}</div>
                <span className={`text-sm font-medium hidden sm:inline ${step >= n ? 'text-stone-900' : 'text-stone-400'}`}>{l}</span>
              </div>
              {i < stepLabels.length - 1 && <div className={`w-8 h-0.5 ${step > n ? 'bg-cafa-red' : 'bg-stone-200'}`} />}
            </React.Fragment>
          ))}
        </div>

        {/* STEP 1: Contact Info */}
        {step === 1 && (
          <Card className="border-stone-200 shadow-sm">
            <CardContent className="p-6 space-y-5">
              <h2 className="text-2xl font-serif text-stone-900 mb-2">Your Information</h2>
              <p className="text-stone-500 text-sm mb-4">You do not need an account to submit. Just provide your contact details.</p>
              <div>
                <Label className="text-base font-medium flex items-center gap-2"><User size={16} /> Full Name *</Label>
                <Input className="text-lg h-12 mt-1" placeholder="Your full name" value={guestName} onChange={e => setGuestName(e.target.value)} data-testid="guest-name" />
              </div>
              <div>
                <Label className="text-base font-medium flex items-center gap-2"><Mail size={16} /> Email *</Label>
                <Input className="text-lg h-12 mt-1" type="email" placeholder="you@example.com" value={guestEmail} onChange={e => setGuestEmail(e.target.value)} data-testid="guest-email" />
              </div>
              <div>
                <Label className="text-base font-medium flex items-center gap-2"><Phone size={16} /> Phone (optional)</Label>
                <Input className="text-lg h-12 mt-1" type="tel" placeholder="(210) 555-1234" value={guestPhone} onChange={e => setGuestPhone(e.target.value)} data-testid="guest-phone" />
              </div>
              {paymentRequired && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-800">
                  Non-member submission fee: <strong>${totalFee.toFixed(2)}</strong> (PayPal payment required)
                </div>
              )}
              <Button size="lg" className="w-full h-14 text-lg bg-cafa-red hover:bg-red-700 text-white" onClick={goToStep2} data-testid="continue-to-artwork">
                Continue <ArrowRight size={20} className="ml-2" />
              </Button>
            </CardContent>
          </Card>
        )}

        {/* STEP 2: Artworks */}
        {step === 2 && (
          <div className="space-y-6">
            {artworks.map((aw, i) => (
              <Card key={i} className="border-stone-200 shadow-sm" data-testid={`artwork-block-${i}`}>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-serif text-stone-900">Artwork {i + 1}</h3>
                    {artworks.length > 1 && <Button variant="ghost" size="sm" className="text-red-500" onClick={() => removeArtwork(i)}><Trash2 size={16} className="mr-1" /> Remove</Button>}
                  </div>
                  <div className="space-y-4">
                    <div>
                      <Label className="text-base font-medium">Title *</Label>
                      <Input className="text-lg h-12 mt-1" placeholder="e.g. Morning Light" value={aw.title} onChange={e => updateArtwork(i, 'title', e.target.value)} data-testid={`artwork-title-${i}`} />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <div><Label className="text-base font-medium">Size *</Label><Input className="text-lg h-12 mt-1" placeholder='e.g. 16"x20"' value={aw.size} onChange={e => updateArtwork(i, 'size', e.target.value)} data-testid={`artwork-size-${i}`} /></div>
                      <div><Label className="text-base font-medium">Medium *</Label><Input className="text-lg h-12 mt-1" placeholder="e.g. Oil on canvas" value={aw.medium} onChange={e => updateArtwork(i, 'medium', e.target.value)} data-testid={`artwork-medium-${i}`} /></div>
                      <div><Label className="text-base font-medium">Price ($) *</Label><Input className="text-lg h-12 mt-1" type="number" min="0" step="1" placeholder="e.g. 500" value={aw.price} onChange={e => updateArtwork(i, 'price', e.target.value)} data-testid={`artwork-price-${i}`} /></div>
                    </div>
                    {categories.length > 0 && (
                      <div>
                        <Label className="text-base font-medium">Medium Category *</Label>
                        <select className="w-full text-lg h-12 mt-1 border rounded-md px-3 bg-white" value={aw.medium_category_id} onChange={e => updateArtwork(i, 'medium_category_id', e.target.value)} data-testid={`artwork-category-${i}`}>
                          <option value="">Select a category...</option>
                          {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                      </div>
                    )}
                    <div>
                      <Label className="text-base font-medium mb-2 block">Images *</Label>
                      {aw.image_urls.length > 0 && (
                        <div className="flex flex-wrap gap-3 mb-3">
                          {aw.image_urls.map((url, j) => (
                            <div key={j} className="relative w-24 h-24 rounded-lg overflow-hidden border-2 border-stone-200 group">
                              <img src={url} alt={`Upload ${j+1}`} className="w-full h-full object-cover" />
                              <button onClick={() => removeImage(i, j)} className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"><X size={12} /></button>
                            </div>
                          ))}
                        </div>
                      )}
                      <div
                        className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${aw.uploading ? 'border-cafa-red bg-red-50' : 'border-stone-300 hover:border-cafa-red hover:bg-red-50/50'}`}
                        onClick={() => fileInputRefs.current[i]?.click()}
                        onDragOver={e => { e.preventDefault(); e.currentTarget.classList.add('border-cafa-red', 'bg-red-50'); }}
                        onDragLeave={e => { e.currentTarget.classList.remove('border-cafa-red', 'bg-red-50'); }}
                        onDrop={e => { e.preventDefault(); e.currentTarget.classList.remove('border-cafa-red', 'bg-red-50'); handleImageUpload(i, e.dataTransfer.files); }}
                        data-testid={`upload-zone-${i}`}
                      >
                        <input ref={el => fileInputRefs.current[i] = el} type="file" multiple accept="image/jpeg,image/png,image/webp" className="hidden" onChange={e => { handleImageUpload(i, e.target.files); e.target.value = ''; }} />
                        {aw.uploading
                          ? <div className="flex flex-col items-center gap-2"><Loader2 size={32} className="animate-spin text-cafa-red" /><p className="text-cafa-red font-medium">Uploading...</p></div>
                          : <div className="flex flex-col items-center gap-2"><Upload size={36} className="text-stone-400" /><p className="text-lg font-medium text-stone-700">Drag & drop images here</p><p className="text-stone-500">or click to browse</p><p className="text-sm text-stone-400 mt-1">JPEG, PNG, or WebP &bull; Max 3MB each</p></div>
                        }
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
            {artworks.length < (call?.max_artworks || 5) && (
              <Button variant="outline" size="lg" className="w-full h-14 text-lg border-dashed border-2" onClick={addArtwork} data-testid="add-artwork-btn"><Plus size={20} className="mr-2" /> Add Another Artwork</Button>
            )}
            <p className="text-center text-stone-400 text-sm">{artworks.length} of {call?.max_artworks || 5} artworks</p>
            <Button size="lg" className="w-full h-14 text-lg bg-cafa-red hover:bg-red-700 text-white" onClick={goToStep3} data-testid="continue-to-review">Continue to Review <ArrowRight size={20} className="ml-2" /></Button>
          </div>
        )}

        {/* STEP 3: Review */}
        {step === 3 && (
          <div className="space-y-6">
            <Card className="border-stone-200"><CardContent className="p-6">
              <h2 className="text-2xl font-serif text-stone-900 mb-4">Review Your Submission</h2>
              <div className="bg-stone-50 rounded-lg p-4 mb-6">
                <p className="text-sm text-stone-500">Submitting as</p>
                <p className="font-semibold text-stone-900">{guestName}</p>
                <p className="text-stone-600 text-sm">{guestEmail}{guestPhone ? ` | ${guestPhone}` : ''}</p>
              </div>
              <div className="space-y-4">
                {artworks.map((aw, i) => (
                  <div key={i} className="border rounded-lg p-4" data-testid={`review-artwork-${i}`}>
                    <h3 className="text-lg font-semibold mb-2">{aw.title}</h3>
                    <div className="grid grid-cols-3 gap-3 text-sm text-stone-600 mb-3">
                      <div><span className="text-stone-400">Size:</span> {aw.size}</div>
                      <div><span className="text-stone-400">Medium:</span> {aw.medium}</div>
                      <div><span className="text-stone-400">Price:</span> ${aw.price}</div>
                    </div>
                    <div className="flex flex-wrap gap-2">{aw.image_urls.map((url, j) => <img key={j} src={url} alt="" className="w-20 h-20 object-cover rounded-lg border" />)}</div>
                  </div>
                ))}
              </div>
              {paymentRequired && (
                <div className="bg-stone-50 rounded-lg p-4 mt-6" data-testid="fee-summary">
                  <h4 className="font-medium mb-2">Fee Summary (Non-Member)</h4>
                  <p className="font-bold mt-2">Total: ${totalFee.toFixed(2)}</p>
                </div>
              )}
            </CardContent></Card>
            <div className="flex gap-4">
              <Button variant="outline" size="lg" className="flex-1 h-14 text-lg" onClick={() => setStep(2)} data-testid="back-to-artwork"><ArrowLeft size={18} className="mr-2" /> Edit Artworks</Button>
              <Button size="lg" className="flex-1 h-14 text-lg bg-cafa-red hover:bg-red-700 text-white" onClick={handleSubmit} disabled={submitting} data-testid="final-submit-btn">
                {submitting ? <><Loader2 size={20} className="mr-2 animate-spin" /> Submitting...</>
                  : paymentRequired ? <><CreditCard size={20} className="mr-2" /> Continue to Payment</>
                  : <><Check size={20} className="mr-2" /> Submit Artwork</>}
              </Button>
            </div>
          </div>
        )}

        {/* STEP 4: Payment */}
        {step === 4 && pendingSubmission && (
          <div className="space-y-6" data-testid="payment-step">
            <Card className="border-stone-200"><CardContent className="p-6">
              <div className="text-center mb-6">
                <CreditCard size={40} className="mx-auto text-cafa-red mb-3" />
                <h2 className="text-2xl font-serif text-stone-900">Payment Required</h2>
                <p className="text-stone-600 mt-1">Complete payment to finalize your submission</p>
              </div>
              <div className="bg-stone-50 rounded-lg p-5 mb-6" data-testid="fee-breakdown">
                <h3 className="text-sm font-medium text-stone-500 uppercase tracking-wide mb-3">Non-Member Fee</h3>
                <div className="border-t border-stone-200 pt-2 flex justify-between font-semibold text-stone-900 text-lg">
                  <span>Total</span><span>${(pendingSubmission.total_fee || 0).toFixed(2)}</span>
                </div>
              </div>
              <SubmissionPayPal
                onCreateOrder={async () => {
                  const res = await fetch(`${API_URL}/api/submissions/guest/pay/${pendingSubmission.id}`, {
                    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ token: guestToken })
                  });
                  if (!res.ok) throw new Error('Failed to create payment');
                  return await res.json();
                }}
                onApprove={async (orderId, capabilityToken) => { await capturePayment(orderId, capabilityToken); }}
                onError={() => { toast.error('Payment cancelled or failed. Try again.'); }}
              />
              <p className="text-center text-stone-400 text-xs mt-4">Your submission will be finalized after payment.</p>
            </CardContent></Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default GuestSubmissionForm;
